/**
 * Order Card Component
 *
 * Displays a single order in card format.
 * Extracted from OrdersList for better maintainability.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@uncinc/uncinc-react-kitchen-sink';

import {
  formatPrice,
  formatDate,
  getProductFallbackIcon,
} from '../../../../../../helpers/formatting';
import { orderHasLicenseManagement } from '../../../../../../helpers/orders';

import type { Order } from '../../../../../../types/orders';

import './style.scss';

interface OrderCardProps {
  order: Order;
  onViewDetails: (orderId: number) => void;
  onManageSeats: (orderId: number) => void;
}

/**
 * Meta item data structure
 */
interface MetaItem {
  key: string;
  value: string;
  mobileLabel?: string;
}

const OrderCard: React.FC<OrderCardProps> = ({
  order,
  onViewDetails,
  onManageSeats,
}) => {
  const { t } = useTranslation();

  // License management (invitations) only applies to e-learning orders.
  // Trainings, physical products and digital tools never show it, even when
  // ordered in quantities greater than one.
  const hasInvites = orderHasLicenseManagement(order);

  // Build meta items array (single source of truth)
  const metaItems: MetaItem[] = [];

  if (hasInvites && order.invite_stats) {
    metaItems.push({
      key: 'participants',
      value: t('mijn_vnl.orders.participants_invited', {
        invited: order.invite_stats.invited,
        total: order.invite_stats.total,
      }),
    });
  }

  if (order.total_price) {
    metaItems.push({
      key: 'price',
      value: formatPrice(order.total_price),
    });
  }

  if (order.order_date) {
    metaItems.push({
      key: 'date',
      value: formatDate(order.order_date),
    });
  }

  metaItems.push({
    key: 'orderId',
    value: `#${order.order_number}`,
    mobileLabel: t('mijn_vnl.orders.order_number', { id: order.order_number }),
  });

  // "Bekijk details" is hidden for digital tools (no detail page to show).
  const showDetailsLink = order.product_type !== 'digital_tool';
  const hasActions = hasInvites || showDetailsLink;

  // Multi-product orders show a generic count instead of a single product
  // title, since no single product represents the order.
  const isMultiProduct = (order.item_count ?? 1) > 1;
  const cardTitle = isMultiProduct
    ? t('mijn_vnl.orders.product_count', { count: order.item_count })
    : order.product_title || t('mijn_vnl.orders.untitled_product');

  return (
    <div className="OrderCard">
      {/* Product image, or a basic type icon when none is available */}
      {order.product_image_url ? (
        <div className="OrderCard__image">
          <img src={order.product_image_url} alt={order.product_title || ''} />
        </div>
      ) : (
        <div className="OrderCard__image-fallback">
          <i
            className={`icon ${getProductFallbackIcon(
              order.product_type,
            )} icon-variant-2 OrderCard__image-fallback__icon`}
            aria-hidden="true"
          />
        </div>
      )}

      <div className="OrderCard__content">
        <div className="OrderCard__top">
          {/* Product Title */}
          <h4 className="OrderCard__title">{cardTitle}</h4>

          {/* Meta Information - Desktop (inline with dot separators) */}
          <div className="OrderCard__meta">
            {metaItems.map((item, index) => (
              <React.Fragment key={item.key}>
                <span className="OrderCard__meta-item">{item.value}</span>
                {index < metaItems.length - 1 && (
                  <span className="OrderCard__meta-separator">•</span>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Meta Information - Mobile (stacked vertically) */}
          <div className="OrderCard__meta-stacked">
            {metaItems.map((item) => (
              <span key={item.key} className="OrderCard__meta-stacked__item">
                {item.mobileLabel || item.value}
              </span>
            ))}
          </div>
        </div>

        {/* Actions Row */}
        {hasActions && (
          <div className="OrderCard__actions">
            {/* "Beheer licenties" button only for multi-seat/someone else orders */}
            {hasInvites && (
              <Button
                className="OrderCard__button"
                onClick={() => onManageSeats(order.order_id)}
              >
                {t('mijn_vnl.orders.manage_seats')}
              </Button>
            )}

            {showDetailsLink && (
              <button
                type="button"
                className="OrderCard__link"
                onClick={() => onViewDetails(order.order_id)}
              >
                {t('mijn_vnl.orders.view_details')}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderCard;
